import { useCallback, useState } from 'react';
import { css } from '@emotion/react';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { AnimatePresence, motion } from 'motion/react';
import { TagsV2 } from '@frontend/api-tag';
import { useTranslation } from '@frontend/i18n';
import { ScopedLocationFilterMenu } from '@frontend/location-filter-menu';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import { isPartialPhoneNumber, isPhoneNumber, sanitizePhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Button, SearchField, useFormField, useModalControl } from '@frontend/design-system';
import {
  AllCallsTable,
  usePhonePageSettingShallowStore,
  VoicemailFiltersType,
} from '../../hooks/use-phone-config-state-store';
import { useVoicemailBoxList } from '../../hooks/use-voicemail-box';
import { defaultDateRangeMap } from '../../utils';
import { MessageStatus } from '../all-calls/types';
import { getTimePeriod } from '../all-calls/utils';
import { DEFAULT_NO_TIME_PERIOD } from '../filter-selectors/time-period-selector';
import { VoicemailsTrayFilters } from './vm-filters';
import { hasVMFilterChanged } from './voicemail-utils';
import { SetPageConfigInput } from './voicemails-multi.component';

interface VoicemailActionBarProps {
  setSelectedTimerange: (subtitle: string) => void;
}

const getDefaultFilters: (sipId: string) => VoicemailFiltersType = (sipId) => ({
  locationIds: [],
  syncDeviceSipProfileId: sipId,
  filterOptions: {
    callerNumber: '',
    callerName: '',
    endTime: defaultDateRangeMap[DEFAULT_NO_TIME_PERIOD].endDate,
    startTime: defaultDateRangeMap[DEFAULT_NO_TIME_PERIOD].startDate,
    status: MessageStatus.MESSAGE_STATUS_ALL,
    mailboxIds: [],
    tagIds: [],
  },
});

export const VoicemailActionBar = ({ setSelectedTimerange }: VoicemailActionBarProps) => {
  const { selectedLocationIds: selectedLocations, selectedOrgId } = useAppScopeStore();
  const [shouldShowSearch, setShouldShowSearch] = useState(false);
  const { modalProps, triggerProps } = useModalControl();
  const { t } = useTranslation('calls');

  const {
    config,
    setPageSize,
    setPageNumber,
    setFilters: setVoicemailPageFilters,
  } = usePhonePageSettingShallowStore('config', 'setPageSize', 'setPageNumber', 'setFilters');
  const pageNumber = config[AllCallsTable.Voicemails].pageNumber;
  const pageSize = config[AllCallsTable.Voicemails].pageSize;
  const { phoneConfig } = usePhoneConfigShallowStore('phoneConfig');
  const { data: voicemailBoxList } = useVoicemailBoxList({
    phoneConfig,
    locationIds: selectedLocations,
  });
  const { data: mappedTags = {} } = TagsV2.Queries.useListTagsQuery({
    request: { orgId: selectedOrgId, groupIds: selectedLocations },
    options: {
      select: (data) => {
        return data?.tags.reduce<Record<string, Tag>>((acc, tag) => {
          acc[tag.id] = tag;
          return acc;
        }, {});
      },
    },
  });

  const isMultiLocationSelected = selectedLocations.length > 1;
  const setPageConfig = useCallback(
    (input: SetPageConfigInput) => {
      const data = typeof input === 'function' ? input({ pageSize, pageNumber }) : input;
      if (data.pageSize) setPageSize(AllCallsTable.Voicemails, data.pageSize);
      if (data.pageNumber) setPageNumber(AllCallsTable.Voicemails, data.pageNumber);
    },
    [setPageSize, setPageNumber, pageSize, pageNumber]
  );
  const defaultFilters = getDefaultFilters(phoneConfig?.sipProfileId ?? '');

  const filtersInStore = config[AllCallsTable.Voicemails].filters;
  const filters = filtersInStore || defaultFilters;
  const setFilters = useCallback(
    (filters: VoicemailFiltersType) => {
      setVoicemailPageFilters(AllCallsTable.Voicemails, filters);
      // Reset page number to 1 when filters are changed
      setPageConfig({ pageNumber: 1 });
      setSelectedTimerange(getTimePeriod(filters.filterOptions.startTime ?? '', filters.filterOptions.endTime ?? ''));
    },
    [setVoicemailPageFilters, setPageConfig, setSelectedTimerange]
  );

  const searchHandler = useCallback(
    (searchText: string) => {
      const sanitizedNumber = sanitizePhoneNumber(searchText);
      const numberSearch =
        isPhoneNumber(sanitizedNumber) || isPartialPhoneNumber(sanitizedNumber) ? sanitizedNumber : '';
      const nameSearch = !isPhoneNumber(sanitizedNumber) && !isPartialPhoneNumber(sanitizedNumber) ? searchText : '';
      setFilters({
        ...filters,
        filterOptions: {
          ...filters.filterOptions,
          callerNumber: numberSearch,
          callerName: nameSearch,
        },
      });
    },
    [filters, setFilters]
  );

  const onLocationChange = useCallback(
    (locations: string[]) => {
      setFilters({
        ...filters,
        locationIds: locations,
      });
    },
    [filters, setFilters]
  );

  const onSearchClick = useCallback(() => {
    setShouldShowSearch(true);
  }, [setShouldShowSearch]);

  const onDismissClick = useCallback(() => setShouldShowSearch(false), [setShouldShowSearch]);

  const showSearchBadge = !!filters.filterOptions.callerName;
  const showFilterBadge = hasVMFilterChanged(filters, defaultFilters);

  const searchField = useFormField(
    { type: 'text', placeholder: t('Search name or 10-digit number'), value: filters.filterOptions.callerName },
    [filters.filterOptions.callerName]
  );

  const clearField = useCallback(() => {
    searchField.onChange({
      name: 'search',
      value: '',
    });
    searchHandler('');
  }, [searchField]);

  if (shouldShowSearch) {
    return (
      <AnimatePresence>
        <motion.div
          variants={!isMultiLocationSelected ? searchContainerVariants : {}}
          initial='hidden'
          animate='visible'
          exit='exit'
          css={[
            css`
              display: grid;
              grid-template-columns: 1fr auto;
              align-items: center;
              gap: ${theme.spacing(1)};
              position: relative;
            `,
            !isMultiLocationSelected &&
              css`
                width: 100%;
              `,
          ]}
        >
          <SearchField
            {...searchField}
            autoFocus
            css={
              !isMultiLocationSelected &&
              css`
                border-radius: ${theme.spacing(2)};
                height: 48px;
                box-shadow: ${theme.shadows.floating};
              `
            }
            containerCss={[
              !isMultiLocationSelected &&
                css`
                  & > div:focus-within {
                    box-shadow: ${theme.shadows.floating} !important;
                    border-color: ${theme.colors.neutral30} !important;
                  }
                `,
            ]}
            onChange={(e) => {
              searchField.onChange(e);

              if (!e) {
                return;
              }

              if ('target' in e) {
                searchHandler((e.target as HTMLInputElement).value);
              } else {
                searchHandler(e.value);
              }
            }}
            label=''
            name='search'
            clearable={isMultiLocationSelected}
          />
          {!isMultiLocationSelected ? (
            <div
              css={css`
                display: flex;
                align-items: center;
                position: absolute;
                right: ${theme.spacing(3)};
              `}
            >
              {!!searchField.value && (
                <Button
                  css={css`
                    border: none;
                    padding: 0;
                    color: ${theme.colors.primary50};
                    margin-right: ${theme.spacing(1.5)};
                  `}
                  onClick={clearField}
                  variant='secondary'
                >
                  {t('Clear')}
                </Button>
              )}
              <Button
                css={css`
                  padding: 0;
                  &:hover {
                    background: transparent;
                  }
                `}
                onClick={onDismissClick}
                variant='secondary'
                iconName='x'
              />
            </div>
          ) : (
            <Button variant='secondary' size='large' onClick={onDismissClick}>
              {t('Done')}
            </Button>
          )}
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: selectedLocations.length > 1 ? '1fr auto auto' : 'auto auto auto 1fr',
        gridTemplateAreas: selectedLocations.length > 1 ? '"location search filter"' : '"location search filter ."',
      }}
    >
      <ScopedLocationFilterMenu
        selectedOptions={selectedLocations}
        trackingIdBase={'phone-weave2.0-voicemails'}
        onChange={onLocationChange}
        showClearAll={false}
        css={{ gridArea: 'location' }}
      />
      <Button
        variant='secondary'
        iconName='search'
        size='large'
        css={[
          { gridArea: 'search' },
          css`
            ::after {
              position: absolute;
              opacity: ${showSearchBadge ? 1 : 0};
              background: ${theme.colors.primary50};
              border-radius: 16px;
              border: 1.5px solid ${theme.colors.white};
              height: ${theme.spacing(1.5)};
              width: ${theme.spacing(1.5)};
              top: 6px;
              left: ${theme.spacing(3)};
              z-index: ${theme.zIndex.high};
              content: '';
              transition: border-color, opacity ease-in-out 250ms;
            }
            :hover::after,
            :focus::after {
              border-color: ${theme.colors.neutral10};
            }
          `,
        ]}
        onClick={onSearchClick}
      />
      <Button
        variant='secondary'
        iconName='filter-alt'
        size='large'
        css={[
          { gridArea: 'filter' },
          css`
            ::after {
              position: absolute;
              opacity: ${showFilterBadge ? 1 : 0};
              background: ${theme.colors.primary50};
              border-radius: 16px;
              border: 1.5px solid ${theme.colors.white};
              height: ${theme.spacing(1.5)};
              width: ${theme.spacing(1.5)};
              top: 6px;
              left: ${theme.spacing(3)};
              z-index: ${theme.zIndex.high};
              content: '';
              transition: border-color, opacity ease-in-out 250ms;
            }
            :hover::after,
            :focus::after {
              border-color: ${theme.colors.neutral10};
            }
          `,
        ]}
        {...triggerProps}
      />
      <VoicemailsTrayFilters
        defaultFilters={defaultFilters}
        onChange={setFilters}
        isMulti={selectedLocations.length > 1}
        resetFilters={() => setFilters(defaultFilters)}
        modalProps={modalProps}
        filters={filters}
        mailboxes={voicemailBoxList}
        mappedTags={mappedTags}
      />
    </div>
  );
};

const searchContainerVariants = {
  hidden: {
    opacity: 0,
    y: -8,
    scale: 0.98,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  exit: {
    opacity: 0,
    y: -6,
    scale: 0.98,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 1, 1],
    },
  },
};
